import { useQuery } from 'react-query'
import { useAuth0 } from '../../../contexts/auth0-context'
import callAPI from '../../../API/callSecureAPI'

export type ActivityFeedItem = {
    _id: string
    action: string
    timestamp: string
    user: {
        name: string
        email: string
    } | null
    organization: {
        _id: string
        name: string
    } | null
    roadmap: {
        _id: string
        product: string
        indication: string
    } | null
    itemType: string | null
    itemName: string | null
    metadata: Record<string, unknown> | null
}

export type ActivityFeedResponse = {
    success: boolean
    data: {
        feed: ActivityFeedItem[]
        actionTypes: string[]
        pagination: {
            page: number
            limit: number
            total: number
            totalPages: number
        }
    }
}

const FIVE_MINUTES = 1000 * 60 * 5

export const useAdminActivityFeedQuery = (
    page: number = 1,
    limit: number = 100,
    actionFilter?: string,
    orgFilter?: string,
    startDate?: string,
    endDate?: string,
    roadmapFilter?: string
) => {
    const { getTokenSilently, isAuthenticated, isLoading } = useAuth0()

    const getActivityFeed = async () => {
        try {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })
            let url = `/api/admin/activity-feed?page=${page}&limit=${limit}`
            if (actionFilter) url += `&action=${actionFilter}`
            if (orgFilter) url += `&orgId=${orgFilter}`
            if (startDate) url += `&startDate=${startDate}`
            if (endDate) url += `&endDate=${endDate}`
            if (roadmapFilter) url += `&roadmapId=${roadmapFilter}`
            const response = await callAPI(url, 'get', token)
            return response
        } catch (e) {
            console.error('Error fetching activity feed:', e)
            throw e
        }
    }

    return useQuery<ActivityFeedResponse>(
        ['admin', 'activity-feed', page, limit, actionFilter, orgFilter, startDate, endDate, roadmapFilter],
        getActivityFeed,
        {
            enabled: !!getTokenSilently && !!isAuthenticated && !isLoading,
            cacheTime: FIVE_MINUTES,
            staleTime: 30000,
            retry: 2,
        }
    )
}
